import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ROLE_OPTIONS, roleLabel } from "@/lib/utils-msip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, MapPin, Pencil } from "lucide-react";
import { BranchDealerMapping } from "@/pages/AdminManpower";
import { BayCell, BayDialog } from "@/components/BayDialog";
import { TdCarsCell, TdCarsDialog } from "@/components/TdCarsDialog";
import { UserEditDialog, ScopePicker, scopeKind, allowedDealerCodes } from "@/components/UserEditDialog";

const DISTRICTS = ["ALLEPPEY", "ERNAKULAM", "IDUKKI", "KANNUR", "KASARAGOD", "KOLLAM", "KOTTAYAM", "KOZHIKODE", "MALAPPURAM", "PALAKKAD", "PATHANAMTHITTA", "TRICHUR", "TRIVANDRUM", "WAYANAD"];
const FACILITIES = ["1S", "2S", "3S", "Studio", "Festive"];
const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground";

export function Dealers() {
  const { meta } = useApp();
  const [dealers, setDealers] = useState([]);
  const [form, setForm] = useState({ code: "", name: "", district: "ERNAKULAM", facility: "3S" });
  const [tagging, setTagging] = useState(null);
  const [tagSel, setTagSel] = useState([]);
  const [tagSearch, setTagSearch] = useState("");
  const [baying, setBaying] = useState(null);
  const [tding, setTding] = useState(null);

  const load = useCallback(() => api.get("/admin/dealers").then(({ data }) => setDealers(data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const keralaRtos = useMemo(
    () => (meta?.geographies || []).filter((g) => !g.is_state_row).map((g) => g.rto_code).sort(),
    [meta]
  );

  const create = async () => {
    try {
      await api.post("/admin/dealers", form);
      toast.success(`Dealer ${form.code.toUpperCase()} added`);
      setForm({ code: "", name: "", district: "ERNAKULAM", facility: "3S" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const update = async (code, patch) => {
    try {
      await api.put(`/admin/dealers/${encodeURIComponent(code)}`, patch);
      toast.success(`${code} updated`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete dealer ${code}? Its RTO tags and user mappings will be removed.`)) return;
    try {
      await api.delete(`/admin/dealers/${encodeURIComponent(code)}`);
      toast.success(`${code} deleted`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveTags = async () => {
    await update(tagging.code, { rto_codes: tagSel });
    setTagging(null);
  };

  return (
    <div className="space-y-4">
      <div className="border bg-card rounded-md p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">Dealer code</label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="INC-XYZ" className="h-9 w-32 text-sm" data-testid="dealer-code-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">Name</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Incheon Kia …" className="h-9 w-52 text-sm" data-testid="dealer-name-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">District</label>
          <Select value={form.district} onValueChange={(v) => setForm({ ...form, district: v })}>
            <SelectTrigger className="h-9 w-44 text-sm" data-testid="dealer-district-select"><SelectValue /></SelectTrigger>
            <SelectContent>{DISTRICTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">Facility</label>
          <Select value={form.facility} onValueChange={(v) => setForm({ ...form, facility: v })}>
            <SelectTrigger className="h-9 w-28 text-sm" data-testid="dealer-facility-select"><SelectValue /></SelectTrigger>
            <SelectContent>{FACILITIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={create} disabled={!form.code || !form.name} data-testid="dealer-add-btn">Add dealer</Button>
      </div>

      <div className="border bg-card rounded-md overflow-auto max-h-[65vh]">
        <table className="w-full text-sm">
          <thead className="border-b sticky top-0 bg-card">
            <tr>{["Code", "Name", "District", "Facility", "Workshop", "Bays", "TD cars", "Mapped RTOs", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {dealers.map((d) => (
              <tr key={d.code} className="border-b last:border-0 hover:bg-muted/50" data-testid={`dealer-row-${d.code}`}>
                <td className="px-3 py-1.5 font-semibold whitespace-nowrap">{d.code}</td>
                <td className="px-3 py-1.5">{d.name}</td>
                <td className="px-3 py-1.5">
                  <Select value={DISTRICTS.includes(d.district) ? d.district : undefined} onValueChange={(v) => update(d.code, { district: v })}>
                    <SelectTrigger className="h-7 w-40 text-xs" data-testid={`dealer-district-${d.code}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{DISTRICTS.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-1.5">
                  <Select value={d.facility} onValueChange={(v) => update(d.code, { facility: v })}>
                    <SelectTrigger className="h-7 w-24 text-xs" data-testid={`dealer-facility-${d.code}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{FACILITIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-1.5">
                  <Switch checked={!!d.has_workshop} onCheckedChange={(v) => update(d.code, { has_workshop: v })} data-testid={`dealer-workshop-${d.code}`} />
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <BayCell dealer={d} onOpen={() => setBaying(d)} />
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <TdCarsCell dealer={d} onOpen={() => setTding(d)} />
                </td>
                <td className="px-3 py-1.5">
                  <button
                    onClick={() => { setTagging(d); setTagSel(d.rto_codes || []); setTagSearch(""); }}
                    className="flex items-center gap-1 rounded border px-2 py-1 text-xs hover:bg-secondary transition-colors duration-200"
                    data-testid={`dealer-tag-btn-${d.code}`}
                  >
                    <MapPin size={12} /> {(d.rto_codes || []).length} RTOs
                  </button>
                </td>
                <td className="px-3 py-1.5 text-right">
                  <button onClick={() => remove(d.code)} className="text-muted-foreground hover:text-red-600 transition-colors duration-200" data-testid={`dealer-delete-${d.code}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">Initial dealer network seeded from your list (all 3S) with a best-guess RTO mapping around each location — adjust facilities (1S / 2S / 3S / Studio / Festive) and RTO tags as needed. An RTO can be tagged to more than one dealer. <b>Workshop</b> marks the dealers that run an after-sales workshop and must upload the daily DMS reports (Service Cockpit). <b>Bays</b> captures GS and Body &amp; Paint bays (2S / 3S only, effective-dated) for the ROs / bay / day productivity metrics. <b>TD cars</b> captures the test-drive fleet per dealer code (effective-dated) for the Sales TDs / car / day metric.</p>

      <BranchDealerMapping />

      <BayDialog dealer={baying} onClose={() => setBaying(null)} onSaved={load} />
      <TdCarsDialog dealer={tding} onClose={() => setTding(null)} onSaved={load} />

      <Dialog open={!!tagging} onOpenChange={(o) => !o && setTagging(null)}>
        <DialogContent className="max-w-md" data-testid="dealer-tag-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">RTOs for {tagging?.code} — {tagging?.name}</DialogTitle>
            <DialogDescription className="text-xs">Tick every RTO this dealership covers. Sales Managers mapped to this dealer will see exactly these markets.</DialogDescription>
          </DialogHeader>
          <Input placeholder="Search RTO…" value={tagSearch} onChange={(e) => setTagSearch(e.target.value)} className="h-9 text-sm" data-testid="tag-search-input" />
          <div className="max-h-72 overflow-auto border rounded-md divide-y">
            {keralaRtos.filter((r) => !tagSearch || r.toLowerCase().includes(tagSearch.toLowerCase())).map((r) => (
              <label key={r} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/50">
                <input
                  type="checkbox"
                  checked={tagSel.includes(r)}
                  onChange={(e) => setTagSel(e.target.checked ? [...tagSel, r] : tagSel.filter((x) => x !== r))}
                  data-testid={`tag-rto-${r}`}
                />
                {r}
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{tagSel.length} selected</span>
            <Button onClick={saveTags} data-testid="tag-save-btn">Save mapping</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function Users() {
  const { user: me } = useApp();
  const [users, setUsers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", roleKey: "manager_sales", branch_names: [], dealer_codes: [] });
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/dealers").then(({ data }) => setDealers(data)).catch(() => {});
    api.get("/hr/branches").then(({ data }) => setBranches(data.branches || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const opt = ROLE_OPTIONS.find((o) => o.key === form.roleKey);
  const kind = scopeKind(opt);
  const needsBranches = kind === "branches";
  const needsDealers = kind === "dealers" || kind === "workshops";

  const create = async () => {
    try {
      await api.post("/admin/users", {
        name: form.name, email: form.email, password: form.password,
        role: opt.role, department: opt.department,
        branch_names: needsBranches ? form.branch_names : [],
        dealer_codes: kind ? allowedDealerCodes(kind, dealers, form.dealer_codes) : [],
      });
      toast.success(`Account created for ${form.email}`);
      setForm({ name: "", email: "", password: "", roleKey: "manager_sales", branch_names: [], dealer_codes: [] });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const scope = (u) => {
    if (u.role === "admin") return "Full admin";
    if (u.role === "accounts") return "All data · stock upload · bank terms";
    if (u.role === "stock") return "Whole stock module · retail feed upload";
    if (u.role === "uploader") return "Upload portal only";
    const service = u.department === "service" && (u.role === "zonal" || u.role === "manager");
    if (service) return [...(u.dealer_codes || []), ...(u.branch_names || [])].join(", ") || "No workshops assigned";
    if (u.role === "zonal") return [...(u.branch_names || []), ...(u.dealer_codes || []).map((c) => `${c} (studio)`)].join(", ") || "No outlets assigned";
    if (u.role === "manager") return (u.dealer_codes || []).join(", ") || "No dealers mapped";
    return "All markets";
  };

  return (
    <div className="space-y-4">
      <div className="border bg-card rounded-md p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-9 w-40 text-sm" data-testid="user-name-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">Email</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 w-52 text-sm" data-testid="user-email-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">Password (min 8)</label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-9 w-40 text-sm" data-testid="user-password-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">Role</label>
            <Select value={form.roleKey} onValueChange={(v) => setForm({ ...form, roleKey: v })}>
              <SelectTrigger className="h-9 w-52 text-sm" data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLE_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={create} disabled={!form.email || !form.password || !form.name} data-testid="user-create-btn">Create account</Button>
        </div>
        <ScopePicker kind={kind} branches={branches} dealers={dealers} branchNames={form.branch_names} dealerCodes={form.dealer_codes} onChange={(p) => setForm({ ...form, ...p })} idPrefix="user" />
        <p className="text-xs text-muted-foreground">
          Sales Managers see only their mapped dealers' RTOs · Zonal Managers (Sales) see their assigned outlets · Service Managers and Zonal Managers (Service) see the workshops of their dealer codes · MD &amp; VP see everything.
        </p>
      </div>

      <div className="border bg-card rounded-md overflow-auto max-h-[60vh]">
        <table className="w-full text-sm">
          <thead className="border-b sticky top-0 bg-card">
            <tr>{["Name", "Email", "Role", "Scope", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b last:border-0 hover:bg-muted/50" data-testid={`user-row-${u.email}`}>
                <td className="px-3 py-1.5 font-medium whitespace-nowrap">{u.name}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{u.email}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{roleLabel(u)}</td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground">{scope(u)}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(u)} className="text-muted-foreground hover:text-foreground transition-colors duration-200 mr-2" title="Edit account" data-testid={`user-edit-${u.email}`}>
                    <Pencil size={14} />
                  </button>
                  {u.id !== me?.id && (
                    <button onClick={() => remove(u)} className="text-muted-foreground hover:text-red-600 transition-colors duration-200" data-testid={`user-delete-${u.email}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <UserEditDialog user={editing} me={me} branches={branches} dealers={dealers} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
